import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { userStorage } from '../utils/userStorage';
import dbStorageService from '../services/dbStorageService';
import tokenService from '../services/tokenService';
// Note: validateOAuthState is no longer used - backend handles state validation via JWT
import { validateId } from '../utils/validation';
import { DB_CONSTANTS } from '../services/dbHelpers/dbStorageHelper';
import { apiService } from '../services/apiService';
import {
  AUTH_STATES,
  CONNECTION_STATES,
  LOADING_STATES,
  debounce,
  validateUserData,
  checkServerConnectivity,
  initializeSync,
  performDatabaseCleanup,
  handleAuthErrorFlow,
  handleOfflineAuthenticatedState,
  handleTokenRefresh,
  setupUserAndSync,
  handleAuthCheckError,
  executeLogout,
  getSync
} from './AuthContextHelper';

/**
 * Context for authentication related state and methods
 * @type {React.Context}
 */
const AuthContext = createContext(null);

/**
 * Custom hook to access auth context values and methods
 * @returns {Object} Auth context value object containing:
 * - user: Current user object or null if not authenticated
 * - loading: Boolean indicating if auth operations are in progress
 * - error: Error message if authentication failed
 * - login: Function to handle user login
 * - register: Function to handle user registration
 * - logout: Function to handle user logout
 * - checkAuth: Function to check current auth status
 * - isAuthenticated: Boolean indicating if user is authenticated
 * - isOnline: Boolean indicating if app is online
 * - isPending: Boolean indicating if auth operations are pending
 * - authState: Current authentication state
 * - connectionState: Current connection state
 * - loadingState: Current loading state
 * - socialAuth: Function to handle social authentication
 * - getUserId: Function to get user ID
 * - getLegacyId: Function to get legacy user ID
 * - tokenRefreshInProgress: Boolean indicating if token refresh is in progress
 * @throws {Error} If used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Authentication provider component that manages user authentication state
 * Handles:
 * - Authentication state (login, register, logout)
 * - Online/offline state management
 * - Token refresh and management
 * - User data storage and validation
 * - Network connectivity detection
 * - Database and sync service initialization
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} AuthContext provider
 */
export const AuthProvider = ({ children }) => {
  // Initialize states
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Add a specific state for token refresh operations
  const [tokenRefreshInProgress, setTokenRefreshInProgress] = useState(false);

  // Separate states for different concerns
  const [authState, setAuthState] = useState(() => {
    const savedAuthState = localStorage.getItem('auth_state');
    // Convert old states to new format if needed
    if (savedAuthState === 'offline' || savedAuthState === 'pending') {
      return user ? AUTH_STATES.AUTHENTICATED : AUTH_STATES.UNAUTHENTICATED;
    }
    return savedAuthState || AUTH_STATES.UNAUTHENTICATED;
  });

  const [connectionState, setConnectionState] = useState(
    navigator.onLine ? CONNECTION_STATES.ONLINE : CONNECTION_STATES.OFFLINE
  );

  const [loadingState, setLoadingState] = useState(LOADING_STATES.PENDING);

  // Add event listeners for token refresh events
  useEffect(() => {
    const handleTokenRefreshStart = () => {
      setTokenRefreshInProgress(true);
    };

    const handleTokenRefreshEnd = (event) => {
      setTokenRefreshInProgress(false);
    };

    window.addEventListener('token-refresh-start', handleTokenRefreshStart);
    window.addEventListener('token-refresh-end', handleTokenRefreshEnd);

    return () => {
      window.removeEventListener('token-refresh-start', handleTokenRefreshStart);
      window.removeEventListener('token-refresh-end', handleTokenRefreshEnd);
    };
  }, []);

  // Helper function to update user state and storage
  const updateUser = async (userData) => {
    const validatedData = validateUserData(userData);
    setUser(validatedData);
    if (validatedData) {
      // Use skipEventDispatch=true to prevent infinite loop
      await userStorage.setUserData(validatedData, true);
    } else {
      await userStorage.setUserData(null);
    }
  };

  // Helper function to store auth data (tokens and user info)
  const storeAuthData = useCallback(async (authData) => {
    const { access_token, refresh_token, client_key, user } = authData;

    if (!access_token || !refresh_token || !client_key || !user) {
      return;
    }

    // Store tokens first
    tokenService.setAccessToken(access_token);
    await tokenService.setRefreshTokenAndClientKey(refresh_token, client_key);

    // Validate and store user data
    const validatedUser = validateUserData(user);

    if (!validatedUser) {
      return;
    }

    // Update user data in storage and state
    await userStorage.setUserData(validatedUser, true); // Skip event dispatch
    setUser(validatedUser);

    setAuthState(AUTH_STATES.AUTHENTICATED);
    localStorage.setItem('auth_state', AUTH_STATES.AUTHENTICATED);

    const { syncCoordinator } = await getSync();
    if (syncCoordinator) {
      syncCoordinator.initialize().catch(error => {
      });
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      setLoadingState(LOADING_STATES.PENDING);

      if (authState === AUTH_STATES.AUTHENTICATED) {
        if (handleOfflineAuthenticatedState({
          setConnectionState,
          setLoading,
          setLoadingState
        })) {
          return;
        }
      }

      if (!tokenService.isAccessTokenValid()) {
        try {
          const refreshResult = await handleTokenRefresh({
            authState,
            setTokenRefreshInProgress,
            tokenService,
            setConnectionState,
            setLoading,
            setLoadingState
          });
          if (refreshResult?.offline && authState === AUTH_STATES.AUTHENTICATED) {
            return;
          }
        } catch (error) {
          throw error;
        }
      }

      const userData = await userStorage.getUserData();
      if (!userData) throw new Error('No user data found');

      const validatedData = validateUserData(userData);
      if (!validatedData) throw new Error('Invalid user data format');

      const { syncCoordinator } = await getSync();
      await setupUserAndSync({
        validatedData,
        updateUser,
        setConnectionState,
        syncCoordinator
      });
    } catch (err) {
      const { syncCoordinator } = await getSync();
      await handleAuthCheckError({
        error: err,
        authState,
        userStorage,
        setConnectionState,
        updateUser,
        setAuthState,
        syncCoordinator
      });
    } finally {
      setLoading(false);
      setLoadingState(LOADING_STATES.IDLE);
    }
  }, [authState]);

  // Handle online/offline status
  useEffect(() => {
    const handleOnline = async () => {
      // First update UI to show we detected online state
      setConnectionState(CONNECTION_STATES.ONLINE);

      // Only attempt to check auth if we were previously offline
      if (connectionState === CONNECTION_STATES.OFFLINE) {
        // Verify we can actually reach the server before proceeding
        const isServerReachable = await checkServerConnectivity();

        if (isServerReachable) {
          // Server is reachable, proceed with auth check
          checkAuth();
        } else {
          // Browser says online but server is unreachable
          // Set back to offline state
          setConnectionState(CONNECTION_STATES.OFFLINE);
        }
      }
    };

    const handleOffline = () => {
      setConnectionState(CONNECTION_STATES.OFFLINE);
    };

    const handleVisibilityChange = debounce(async () => {
      if (!document.hidden) {
        // Tab became visible - refresh user data to catch any changes from other tabs
        // This is especially important if user verified email in another tab
        if (authState === AUTH_STATES.AUTHENTICATED) {
          // If we're offline, just update the connection state without changing auth state
          if (!navigator.onLine) {
            setConnectionState(CONNECTION_STATES.OFFLINE);
          } else {
            // Browser reports online, but let's verify server connectivity
            const isServerReachable = await checkServerConnectivity();

            if (isServerReachable) {
              // Only refresh auth if user is not verified. 
              // Verified users don't need their status refreshed on tab switch.
              // This prevents unnecessary page refreshes and disruption for verified users.
              // If user is null but authState is AUTHENTICATED, user?.verified is falsey, triggering recovery.
              if (!user?.verified) {
                // Server is reachable, refresh auth to get latest user data (including verification status)
                // This ensures verification status is up-to-date if user verified in another tab
                checkAuth();
              }
              // If user is already verified, no action needed - avoid unnecessary refresh
            } else {
              // Browser says online but server is unreachable
              // Set to offline state without changing auth state
              setConnectionState(CONNECTION_STATES.OFFLINE);
            }
          }
        }
      }
    }, 1000); // 1 second debounce

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [authState, connectionState, checkAuth, user]); // Add 'user' to dependencies

  const login = useCallback(async (email, password) => {
    // Signal any in-progress logout to abort (prevents race condition)
    window._currentLogoutId = null;

    try {
      const response = await apiService.login(email, password);

      // First store the auth data which sets tokens and user
      await storeAuthData(response.data);

      // Ensure auth state is set
      setAuthState(AUTH_STATES.AUTHENTICATED);
      localStorage.setItem('auth_state', AUTH_STATES.AUTHENTICATED);

      // Initialize sync services
      await initializeSync();

      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed');
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      throw err;
    }
  }, [storeAuthData]);

  const register = useCallback(async (email, password, firstName) => {
    // Signal any in-progress logout to abort (prevents race condition)
    window._currentLogoutId = null;

    try {
      const response = await apiService.register(email, password, firstName);

      // First store the auth data which sets tokens and user
      await storeAuthData(response.data);

      // Ensure auth state is set
      setAuthState(AUTH_STATES.AUTHENTICATED);
      localStorage.setItem('auth_state', AUTH_STATES.AUTHENTICATED);

      // Initialize sync services
      await initializeSync();

      return response.data;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
      setAuthState(AUTH_STATES.UNAUTHENTICATED);
      throw err;
    }
  }, [storeAuthData]);

  const logout = useCallback(async () => {
    const { syncCoordinator, syncService } = await getSync();
    await executeLogout({
      setAuthState,
      updateUser,
      connectionState,
      apiService,
      syncCoordinator,
      syncService,
      tokenService,
      dbStorageService
    });
  }, [connectionState]);

  const socialAuth = useCallback(async (provider, data) => {
    // Signal any in-progress logout to abort (prevents race condition)
    window._currentLogoutId = null;

    try {
      // State validation is handled by the backend via JWT-signed state tokens
      // verified during the /auth/callback redirect (Sprint 49+).

      // Validate we received the expected auth data structure
      if (!data || !data.access_token || !data.refresh_token || !data.client_key || !data.user) {
        throw new Error('Invalid OAuth response data');
      }

      // Store the auth data (tokens and user info)
      await storeAuthData(data);

      // Initialize sync services
      await initializeSync();

      return data;
    } catch (err) {
      setError(err.response?.data?.message || err.message || `${provider} authentication failed`);
      throw err;
    }
  }, [storeAuthData]);

  // Check auth status on mount only
  useEffect(() => {
    const validateInitialState = async () => {
      try {
        setLoading(true);

        // If a logout was in progress when the page reloaded, don't attempt
        // to restore a session — stale tokens may still exist in IndexedDB.
        if (localStorage.getItem('logout_in_progress') === 'true') {
          localStorage.removeItem('logout_in_progress');
          localStorage.setItem('auth_state', AUTH_STATES.UNAUTHENTICATED);
          setAuthState(AUTH_STATES.UNAUTHENTICATED);
          await updateUser(null);
          setLoading(false);
          return;
        }

        // First check if we have valid tokens and stored auth state
        const storedAuthState = localStorage.getItem('auth_state');

        // Initialize storage
        userStorage.initialize();

        // Safely check for refresh token
        let hasRefreshToken = false;
        try {
          hasRefreshToken = await tokenService.hasRefreshToken();
        } catch (tokenError) {
          // Continue with hasRefreshToken as false
        }

        // Safely get user data
        let storedUser = null;
        try {
          storedUser = await userStorage.getUserData();
        } catch (userError) {
          // Continue with storedUser as null
        }

        // If we're offline but have stored auth state, refresh token, and user data, maintain authenticated state
        if (!navigator.onLine && storedAuthState === AUTH_STATES.AUTHENTICATED && (hasRefreshToken || storedUser)) {
          setAuthState(AUTH_STATES.AUTHENTICATED);
          if (storedUser) {
            await updateUser(storedUser);
          }
          setConnectionState(CONNECTION_STATES.OFFLINE);
          setLoading(false);
          return;
        }

        // If we have a stored auth state and either refresh token or user data, try to refresh the token
        if (storedAuthState === AUTH_STATES.AUTHENTICATED && (hasRefreshToken || storedUser)) {
          try {
            if (hasRefreshToken) {
              const refreshData = await tokenService.refreshAccessToken();

              // If we got a successful refresh or we're offline, maintain authenticated state
              if (refreshData || !navigator.onLine) {
                setAuthState(AUTH_STATES.AUTHENTICATED);
                if (storedUser) {
                  await updateUser(storedUser);
                }
                setLoading(false);
                return;
              }
            } else if (storedUser) {
              // If we have user data but no refresh token, maintain authenticated state in offline mode
              setAuthState(AUTH_STATES.AUTHENTICATED);
              await updateUser(storedUser);
              setConnectionState(CONNECTION_STATES.OFFLINE);
              setLoading(false);
              return;
            }
          } catch (refreshError) {
            // If we're offline, maintain authenticated state
            if (!navigator.onLine) {
              setAuthState(AUTH_STATES.AUTHENTICATED);
              if (storedUser) {
                await updateUser(storedUser);
              }
              setConnectionState(CONNECTION_STATES.OFFLINE);
              setLoading(false);
              return;
            }
          }
        }

        // If we get here, we're either online and couldn't refresh the token, or we don't have stored credentials
        setAuthState(AUTH_STATES.UNAUTHENTICATED);
        await updateUser(null);

        // Only clear localStorage if we're sure we're logged out
        if (storedAuthState === AUTH_STATES.AUTHENTICATED) {
          localStorage.setItem('auth_state', AUTH_STATES.UNAUTHENTICATED);
        }
      } catch (error) {
        setAuthState(AUTH_STATES.UNAUTHENTICATED);
        localStorage.setItem('auth_state', AUTH_STATES.UNAUTHENTICATED);
        await updateUser(null);
      } finally {
        setLoading(false);
      }
    };

    validateInitialState();

    const handleUserVerified = () => {
      checkAuth();
    };

    const handleUserDataUpdated = async (event) => {
      if (event.detail?.userData) {
        // Check if the user data is different from the current user data
        // to prevent unnecessary updates
        if (JSON.stringify(event.detail.userData) !== JSON.stringify(user)) {
          setUser(event.detail.userData);
        }
      }
    };

    const handleAuthStateChange = (event) => {
      if (event.detail?.state) {
        setAuthState(event.detail.state);
        localStorage.setItem('auth_state', event.detail.state);
      }
    };

    const handleConnectionStateChange = (event) => {
      if (event.detail?.state) {
        setConnectionState(event.detail.state);
        localStorage.setItem('connection_state', event.detail.state);
      }
    };

    const handleAuthError = async (event) => {
      try {
        const { syncCoordinator } = await getSync();
        await handleAuthErrorFlow({
          setLoading,
          setConnectionState,
          authState,
          tokenService,
          userStorage,
          updateUser,
          setAuthState,
          syncCoordinator,
          dbStorageService
        });
      } catch (error) {
        setAuthState(AUTH_STATES.UNAUTHENTICATED);
        await updateUser(null);
      }
    };

    window.addEventListener('user-verified', handleUserVerified);
    window.addEventListener('user-data-updated', handleUserDataUpdated);
    window.addEventListener('auth-state-change', handleAuthStateChange);
    window.addEventListener('connection-state-change', handleConnectionStateChange);
    window.addEventListener('auth-error', handleAuthError);

    return () => {
      window.removeEventListener('user-verified', handleUserVerified);
      window.removeEventListener('user-data-updated', handleUserDataUpdated);
      window.removeEventListener('auth-state-change', handleAuthStateChange);
      window.removeEventListener('connection-state-change', handleConnectionStateChange);
      window.removeEventListener('auth-error', handleAuthError);
    };
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    checkAuth,
    // Provide separate states for different concerns
    isAuthenticated: authState === AUTH_STATES.AUTHENTICATED,
    isOnline: connectionState === CONNECTION_STATES.ONLINE,
    isPending: loadingState === LOADING_STATES.PENDING,
    // Keep original values for backward compatibility
    authState,
    connectionState,
    loadingState,
    socialAuth,
    getUserId: () => user?.id || null,
    getLegacyId: () => user?.legacyId || null,
    tokenRefreshInProgress,
    // Sync services - null when sync disabled; use getSync() for lazy load when enabled
    syncCoordinator: null,
    syncService: null,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AUTH_STATES, CONNECTION_STATES, LOADING_STATES };
export default AuthContext; 